/**
 * ContactPreferences — formulaire utilisateur de préférences contact omnicanal.
 * Canal préféré, plage horaire, opt-ins RGPD.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MessageSquare, MessageCircle, Clock, Check, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { apiClient, formatApiError } from "@/lib/api";

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "sms", label: "SMS", icon: MessageSquare },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { key: "voice", label: "Appel vocal", icon: Phone },
];

const HOURS = [
  { key: "any", label: "Indifférent" },
  { key: "morning", label: "Matin (8h-12h)" },
  { key: "afternoon", label: "Après-midi (12h-18h)" },
  { key: "evening", label: "Soirée (18h-21h)" },
  { key: "business", label: "Heures ouvrées" },
];

export default function ContactPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/messaging/preferences/me")
      .then(r => setPrefs(r.data))
      .catch(() => toast.error("Impossible de charger vos préférences."))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (v) => setPrefs(s => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/messaging/preferences/me", {
        preferred_channel: prefs.preferred_channel,
        fallback_channel: prefs.fallback_channel || null,
        preferred_hours: prefs.preferred_hours,
        timezone_name: prefs.timezone_name || "Europe/Paris",
        phone_e164: prefs.phone_e164 || null,
        whatsapp_e164: prefs.whatsapp_e164 || null,
        opt_in_marketing: !!prefs.opt_in_marketing,
        opt_in_followups: !!prefs.opt_in_followups,
      });
      toast.success("Préférences mises à jour.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Échec d'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <Card className="p-6 rounded-2xl border-slate-100">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      </Card>
    );
  }

  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid="contact-preferences-card">
      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-1">Préférences de contact</p>
        <h3 className="font-serif italic text-xl text-[#0B3A2A]">Comment souhaitez-vous être joint ?</h3>
        <p className="text-xs text-slate-500 mt-1">
          Nous respecterons votre canal et vos horaires de joignabilité pour toute communication.
        </p>
      </div>

      {/* Canal préféré */}
      <div className="mb-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Canal préféré</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CHANNELS.map(c => {
            const Icon = c.icon;
            const selected = prefs.preferred_channel === c.key;
            return (
              <button
                key={c.key}
                data-testid={`pref-channel-${c.key}`}
                onClick={() => set("preferred_channel")(c.key)}
                className={`p-3 rounded-lg border text-sm inline-flex flex-col items-center gap-1.5 transition ${
                  selected
                    ? "border-[#C9A961] bg-[#C9A961]/5 text-[#0B3A2A]"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-xs">{c.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Plage horaire */}
      <div className="mb-5">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Plage horaire préférée
        </p>
        <select
          data-testid="pref-hours"
          value={prefs.preferred_hours || "business"}
          onChange={(e) => set("preferred_hours")(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#C9A961]"
        >
          {HOURS.map(h => <option key={h.key} value={h.key}>{h.label}</option>)}
        </select>
      </div>

      {/* Numéros */}
      <div className="grid sm:grid-cols-2 gap-4 mb-5">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Téléphone (E.164)</p>
          <input
            type="tel"
            data-testid="pref-phone"
            value={prefs.phone_e164 || ""}
            onChange={(e) => set("phone_e164")(e.target.value)}
            placeholder="+33 6 12 34 56 78"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#C9A961]"
          />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">WhatsApp (E.164)</p>
          <input
            type="tel"
            data-testid="pref-whatsapp"
            value={prefs.whatsapp_e164 || ""}
            onChange={(e) => set("whatsapp_e164")(e.target.value)}
            placeholder="+33 6 12 34 56 78"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#C9A961]"
          />
        </div>
      </div>

      {/* Opt-ins */}
      <div className="space-y-2 mb-5">
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="pref-opt-followups"
            checked={!!prefs.opt_in_followups}
            onChange={(e) => set("opt_in_followups")(e.target.checked)}
            className="mt-1 accent-[#C9A961]"
          />
          <span>J'accepte de recevoir des relances et rappels relatifs à mes dossiers (par le canal préféré ci-dessus).</span>
        </label>
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid="pref-opt-marketing"
            checked={!!prefs.opt_in_marketing}
            onChange={(e) => set("opt_in_marketing")(e.target.checked)}
            className="mt-1 accent-[#C9A961]"
          />
          <span>J'accepte de recevoir occasionnellement des informations sur les services premium CONFILEX.</span>
        </label>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="pref-save"
        className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-[#0B3A2A] text-white text-sm hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-2"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        Enregistrer mes préférences
      </button>
    </Card>
  );
}
